import React, { useState, useEffect } from "react";
import { FiEye, FiSearch, FiMail, FiX, FiAlertCircle, FiCalendar, FiFileText, FiPaperclip, FiShield, FiClock, FiDownload, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { createPortal } from "react-dom";
import "./usercss/records.css";
import { auth, db } from "../../firebase";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { appointmentService } from "../../services/appointmentService";
import Banner from "../staffpages/Banner";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations";

const FIXED_CATEGORIES = [
  "All",
  "Family Planning / Contraceptive Services",
  "STI Testing / Screening",
  "Reproductive Health Consultation"
];

const RECORDS_PER_PAGE = 10;

export default function Records() {
  const { language } = useLanguage();
  const t = translations[language];
  const [searchQuery, setSearchQuery] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activeNav, setActiveNav] = useState("All");
  const [viewing, setViewing] = useState(null);
  const [records, setRecords] = useState([]);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [viewModalPage, setViewModalPage] = useState('details'); // 'details' | 'attachments'
  const [bannerMessage, setBannerMessage] = useState("");
  const [bannerType, setBannerType] = useState("error");
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Blockchain modal state
  const [showBlockchainModal, setShowBlockchainModal] = useState(false);
  const [blockchainViewing, setBlockchainViewing] = useState(null);
  const [tamperAlerts, setTamperAlerts] = useState([]);

  useEffect(() => {
    const loadRecords = async () => {
      setIsLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const uid = auth.currentUser?.uid || user.uid;
        if (uid) {
          // Use server-side status filtering to reduce reads
          const completedAppts = await appointmentService.getUserAppointmentsByStatus(uid, 'COMPLETED');

          const loadedAppointmentIds = new Set(completedAppts.map(appointment => appointment.id));
          const missingParentIds = [...new Set(
            completedAppts
              .filter(appointment => appointment.isFollowUp === true && appointment.originalAppointmentId)
              .map(appointment => appointment.originalAppointmentId)
              .filter(parentId => !loadedAppointmentIds.has(parentId))
          )];
          const missingParentSnapshots = await Promise.all(
            missingParentIds.map(parentId => getDoc(doc(db, "appointments", parentId)))
          );
          const allCompletedAppointments = [...completedAppts];
          missingParentSnapshots.forEach(parentSnapshot => {
            if (!parentSnapshot.exists()) return;
            const parentAppointment = { id: parentSnapshot.id, ...parentSnapshot.data() };
            if (parentAppointment.status === "COMPLETED" && parentAppointment.uid === uid) {
              allCompletedAppointments.push(parentAppointment);
            }
          });

          const completed = allCompletedAppointments.map(a => {
            // Automatic Categorization based on serviceName
            let autoCategory = "Other";
            if (a.serviceName === "Family Planning / Contraceptive Services") {
              autoCategory = "Family Planning / Contraceptive Services";
            } else if (a.serviceName === "STI Testing / Screening") {
              autoCategory = "STI Testing / Screening";
            } else if (a.serviceName === "Reproductive Health Consultation") {
              autoCategory = "Reproductive Health Consultation";
            }

            return {
              id: a.id,
              title: a.serviceName,
              clinic: a.clinicName,
              category: autoCategory,
              reference: a.reference || "N/A",
              status: 'Completed',
              date: new Date(a.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
              apptDate: a.date,
              time: a.time,
              lastModified: a.completedAt?.toDate ? a.completedAt.toDate().toISOString() : 
                            a.updatedAt?.toDate ? a.updatedAt.toDate().toISOString() : 
                            new Date(a.date).toISOString(),
              attachmentNotes: a.attachmentNotes || [],
              attachments: a.attachments || [],
              proName: a.proName,
              serviceDetails: a.serviceDetails || {},
              blockchain: a.blockchain || null,
              isFollowUp: a.isFollowUp === true,
              originalAppointmentId: a.originalAppointmentId || null,
              appointmentStatus: a.status
            };
          });

          const completedById = new Map(completed.map(appointment => [appointment.id, appointment]));
          const followUpsByParentId = new Map();
          completed.forEach(appointment => {
            if (!appointment.isFollowUp || !completedById.has(appointment.originalAppointmentId)) return;
            const followUps = followUpsByParentId.get(appointment.originalAppointmentId) || [];
            followUps.push(appointment);
            followUpsByParentId.set(appointment.originalAppointmentId, followUps);
          });

          const groupedRecords = completed
            .filter(appointment => !(
              appointment.isFollowUp &&
              completedById.has(appointment.originalAppointmentId)
            ))
            .map(appointment => ({
              ...appointment,
              followUps: followUpsByParentId.get(appointment.id) || []
            }));
          setRecords(groupedRecords);
        }
      } catch (err) {
        console.error("Error loading data:", err);
      } finally {
        setIsLoading(false);
        setHasLoaded(true);
      }
    };
    loadRecords();
  }, []);

  // Fetch tamper alerts
  useEffect(() => {
    if (!auth.currentUser) return;
    
    const q = query(
      collection(db, "tamper_alerts"),
      where("status", "==", "active")
    );
    
    const unsubscribe = onSnapshot(q, (snap) => {
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTamperAlerts(data);
    }, (err) => {
      console.error("Error subscribing to tamper alerts:", err);
    });
    
    return () => unsubscribe();
  }, []);

  const filtered = records.filter((r) => {
    const matchesNav = activeNav === "All" ? true : r.category === activeNav;
    const matchesSearch =
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.clinic.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (r.reference && r.reference.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesDate = (!startDate || r.apptDate >= startDate) && 
                        (!endDate || r.apptDate <= endDate);

    return matchesNav && matchesSearch && matchesDate;
  }).sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));

  const noRecords = filtered.length === 0;
  const totalPages = Math.ceil(filtered.length / RECORDS_PER_PAGE);
  const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
  const endIndex = startIndex + RECORDS_PER_PAGE;
  const paginatedRecords = filtered.slice(startIndex, endIndex);

  // Calculate which categories have records
  const availableCategories = records.reduce((acc, record) => {
    if (!acc.includes(record.category)) {
      acc.push(record.category);
    }
    return acc;
  }, []);

  // Create filtered categories list: always include 'All', then include the three categories only if they have records
  const filteredCategories = ["All"].concat(
    FIXED_CATEGORIES.slice(1).filter(cat => availableCategories.includes(cat))
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeNav, searchQuery, startDate, endDate]);

  useEffect(() => {
    // Reset activeNav to "All" if the current activeNav is not in filteredCategories
    if (!filteredCategories.includes(activeNav)) {
      setActiveNav("All");
    }
  }, [filteredCategories, activeNav]);

  return (
    <div className="records-root">
      <div className="records-header">
        <h1>{t.healthRecords}</h1>
        <div className="records-controls">
          <div className="records-search-container">
            <div className="records-search-box">
              <div className="records-search-box-inner">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder={t.searchRecords}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="records-date-range-filter">
              <div className="date-range-inputs">
                <div className="records-date-box">
                  <FiCalendar className="date-icon" />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    title="Start Date"
                  />
                </div>
                <span className="date-separator">to</span>
                <div className="records-date-box">
                  <FiCalendar className="date-icon" />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    title="End Date"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Banner message={bannerMessage} type={bannerType} onClose={() => setBannerMessage("")} />

      <div className="records-workspace">
        <aside className="records-sidebar">
          <ul>
            {filteredCategories.map((item) => (
              <li
                key={item}
                className={activeNav === item ? "active" : ""}
                onClick={() => setActiveNav(item)}
              >
                {item === "All" ? t.all : item}
              </li>
            ))}
          </ul>
        </aside>

        <div className="records-main-workspace">
          {isLoading && !hasLoaded ? (
            <div className="records-skeleton-container">
              {[...Array(5)].map((_, index) => (
                <div className="record-card-skeleton" key={index}>
                  <div className="skeleton skeleton-record-title"></div>
                  <div className="skeleton skeleton-record-ref"></div>
                  <div className="skeleton skeleton-record-pro"></div>
                  <div className="skeleton skeleton-record-date"></div>
                </div>
              ))}
            </div>
          ) : noRecords ? (
            <p className="no-records">{t.noRecordsFound}</p>
          ) : (
            <>
              {totalPages > 1 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <span>Showing {startIndex + 1}-{Math.min(endIndex, filtered.length)} of {filtered.length} records</span>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn pagination-btn--prev"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <FiChevronLeft />
                      Previous
                    </button>

                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNumber) => (
                        <button
                          key={pageNumber}
                          className={`pagination-number ${currentPage === pageNumber ? 'pagination-number--active' : ''}`}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      ))}
                    </div>

                    <button
                      className="pagination-btn pagination-btn--next"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <FiChevronRight />
                    </button>
                  </div>
                </div>
              )}
              
              <div className="records-timeline">
                {paginatedRecords.map((record) => (
                  <React.Fragment key={record.id}>
                  <div 
                    className="record-card-timeline"
                    style={{ position: 'relative' }}
                    onClick={() => setViewing(record)}
                  >
                    {/* Blockchain Badge - Upper Right Corner */}
                    {record.blockchain?.status && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setBlockchainViewing(record);
                          setShowBlockchainModal(true);
                        }}
                        style={{
                          position: 'absolute',
                          top: '60px',
                          right: '12px',
                          fontSize: '12px',
                          padding: '6px 10px',
                          borderRadius: '999px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          cursor: 'pointer',
                          zIndex: 10,
                          ...(tamperAlerts.some(alert => alert.appointmentId === record.id && alert.status === 'active') ? {
                                background: '#fef2f2',
                                color: '#dc2626',
                                border: '1px solid #ef4444'
                              } : record.blockchain.status === 'verified' ? {
                                background: '#ecfdf5',
                                color: '#059669',
                                border: '1px solid #10b981'
                              } : {
                                background: '#f3f4f6',
                                color: '#6b7280',
                                border: '1px solid #9ca3af'
                              })
                        }}
                        title="Click to view blockchain details"
                      >
                        {tamperAlerts.some(alert => alert.appointmentId === record.id && alert.status === 'active') ? (
                          <>
                            <FiAlertCircle size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            Tampered
                          </>
                        ) : (
                          <>
                            {record.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                            {record.blockchain.status}
                          </>
                        )}
                      </span>
                    )}
                    <div className="record-content">
                      <h3 className="record-title">{record.title}</h3>
                      <p className="record-description">
                        Ref # {record.reference || "N/A"}
                        <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                          • {record.category}
                        </span>
                      </p>
                      <p className="record-description" style={{ 
                        fontSize: '12px', 
                        color: '#6b7280', 
                        marginTop: '4px' 
                      }}>
                        With {record.proName || "Unknown Professional"}
                      </p>
                      {record.followUps?.length > 0 && (
                        <span style={{
                          display: 'inline-block',
                          marginTop: '6px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          background: '#dbeafe',
                          color: '#1e40af',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          {record.followUps.length} Follow-up{record.followUps.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                    <div className="record-date-row">
                      {new Date(record.lastModified).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </div>
                  </div>

                  {/* Follow-up cards rendered outside and indented */}
                  {record.followUps?.map(followUp => (
                    <div
                      key={followUp.id}
                      style={{ marginLeft: '32px', marginTop: '8px' }}
                      onClick={() => setViewing(followUp)}
                    >
                      <div
                        className="record-card-timeline"
                        style={{ position: 'relative', borderLeft: '3px solid #93c5fd', cursor: 'pointer' }}
                      >
                        <div className="record-content">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              background: '#dbeafe',
                              color: '#1e40af',
                              fontSize: '11px',
                              fontWeight: '600'
                            }}>
                              ↩ Follow-up
                            </span>
                          </div>
                          <h3 className="record-title">{followUp.title}</h3>
                          <p className="record-description">
                            Ref # {followUp.reference || "N/A"}
                            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                              • {followUp.category}
                            </span>
                          </p>
                          <p className="record-description" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                            With {followUp.proName || "Unknown Professional"}
                          </p>
                        </div>
                        <div className="record-date-row">
                          {new Date(followUp.lastModified).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                  </React.Fragment>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {viewing && (
        <div className="records-modal-backdrop">
          <div className="records-modal-card">
            <button
              className="records-modal-close"
              onClick={() => {
                setViewing(null);
                setViewModalPage('details');
              }}
            >
              <FiX />
            </button>

            {/* Appointment Details at Top */}
            <div className="records-appointment-details-card">
              <div className="records-appointment-details-header">
                <div className="records-appointment-details-icon">
                  <FiCalendar size={20} color="#ffffff" />
                </div>
                <div>
                  <h3 className="records-appointment-details-title">Appointment Details</h3>
                </div>
              </div>
              <div className="records-appointment-details-body">
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Title</span>
                  <span className="records-appointment-detail-value">{viewing.title}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Clinic</span>
                  <span className="records-appointment-detail-value">{viewing.clinic}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Reference #</span>
                  <span className="records-appointment-detail-value">{viewing.reference || "N/A"}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Category</span>
                  <span className="records-appointment-detail-value">{viewing.category}</span>
                </div>
                <div className="records-appointment-detail-item">
                  <span className="records-appointment-detail-label">Date & Time</span>
                  <span className="records-appointment-detail-value">
                    {viewing.date} • {viewing.time}
                  </span>
                </div>
                {viewing.serviceDetails?.duration && (
                  <div className="records-appointment-detail-item">
                    <span className="records-appointment-detail-label">Duration</span>
                    <span className="records-appointment-detail-value">{viewing.serviceDetails.duration}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Tabbed Interface */}
            <div className="records-tabs-container">
              <button
                className={`records-tab ${viewModalPage === 'details' ? 'active' : ''}`}
                onClick={() => setViewModalPage('details')}
              >
                <FiFileText size={16} />
                Consultation
              </button>
              <button
                className={`records-tab ${viewModalPage === 'attachments' ? 'active' : ''}`}
                onClick={() => setViewModalPage('attachments')}
              >
                <FiPaperclip size={16} />
                Findings
              </button>
            </div>

            {/* Tab Content */}
            <div className="records-tab-content">
              {viewModalPage === 'details' ? (
                <div className="consultation-content">
                  {/* Findings Section First */}
                  <div className="findings-section-card">
                    <div className="findings-section-header">
                      <FiFileText size={18} color="#3b82f6" />
                      <h4>Consultation Findings</h4>
                    </div>
                    <div className="findings-section-body">
                      {viewing.attachmentNotes?.length > 0 ? (
                        <div className="findings-list">
                          {viewing.attachmentNotes.map((note, idx) => (
                            <div key={idx} className="finding-item">
                              <div className="finding-item-content">
                                <p>{note.text}</p>
                              </div>
                              <div className="finding-item-footer">
                                <span>By {note.createdBy}</span>
                                <span>{new Date(note.createdAt).toLocaleDateString()}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="findings-empty-state">
                          <FiAlertCircle size={24} color="#94a3b8" />
                          <p>No clinical findings recorded for this visit.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Consultation Information Second */}
                  <div className="consultation-card" style={{ marginTop: '16px' }}>
                    <div className="consultation-card-header">
                      <FiShield size={18} color="#3b82f6" />
                      <h4>Consultation Information</h4>
                    </div>
                    <div className="consultation-card-body">
                      <p className="consultation-info">
                        This consultation was conducted with {viewing.proName || "your healthcare provider"}.
                        For any questions about this visit, please contact the clinic.
                      </p>
                      {viewing.serviceDetails?.notes && (
                        <div className="consultation-notes">
                          <h5>Additional Notes</h5>
                          <p>{viewing.serviceDetails.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="findings-content">
                  <div className="findings-scroll-container">
                    {/* Attachments Section */}
                    <div className="findings-section-card">
                      <div className="findings-section-header">
                        <FiPaperclip size={18} color="#3b82f6" />
                        <h4>Uploaded Documents</h4>
                      </div>
                      <div className="findings-section-body">
                        {viewing.attachments?.length > 0 ? (
                          <div className="attachments-list">
                            {viewing.attachments.map((file, index) => (
                              <a
                                key={index}
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="attachment-item"
                              >
                                <div className="attachment-icon">
                                  <FiPaperclip size={20} color="#2563eb" />
                                </div>
                                <div className="attachment-info">
                                  <div className="attachment-name">{file.fileName || 'Unnamed File'}</div>
                                  <div className="attachment-meta">
                                    <span>{file.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}</span>
                                    <span>•</span>
                                    <span>{new Date(file.uploadedAt).toLocaleDateString()}</span>
                                    <span>•</span>
                                    <span>By {file.uploadedBy}</span>
                                  </div>
                                </div>
                                <div className="attachment-download">
                                  <FiDownload size={16} color="#64748b" />
                                </div>
                              </a>
                            ))}
                          </div>
                        ) : (
                          <div className="findings-empty-state">
                            <FiFileText size={24} color="#94a3b8" />
                            <p>No files uploaded for this visit.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Blockchain Modal */}
      {blockchainViewing && showBlockchainModal && createPortal(
        <div className="records-modal-backdrop">
          <div className="records-modal-card" style={{ maxWidth: '500px' }}>
            <button
              className="records-modal-close"
              onClick={() => {
                setShowBlockchainModal(false);
                setBlockchainViewing(null);
              }}
            >
              <FiX />
            </button>
            
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiShield size={20} color="#2563eb" />
              Blockchain Verification
            </h3>

            {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FiAlertCircle size={16} color="#dc2626" />
                <span style={{ fontSize: '13px', color: '#991b1b', fontWeight: '500' }}>
                  Tampering detected - Data does not match blockchain record
                </span>
              </div>
            )}

            <div style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Status</div>
                <div style={{ fontSize: '14px', fontWeight: '600' }}>
                  <span 
                    style={{
                      fontSize: '12px',
                      padding: '4px 8px',
                      borderRadius: '999px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      ...(tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') ? {
                        background: '#fef2f2',
                        color: '#dc2626',
                        border: '1px solid #ef4444'
                      } : blockchainViewing.blockchain.status === 'verified' ? {
                        background: '#ecfdf5',
                        color: '#059669',
                        border: '1px solid #10b981'
                      } : {
                        background: '#f3f4f6',
                        color: '#6b7280',
                        border: '1px solid #9ca3af'
                      })
                    }}
                  >
                    {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') ? (
                      <>
                        <FiAlertCircle size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                        Tampered
                      </>
                    ) : (
                      <>
                        {blockchainViewing.blockchain.status === 'verified' && <FiShield size={10} style={{ marginRight: '4px', verticalAlign: 'middle' }} />}
                        {blockchainViewing.blockchain.status}
                      </>
                    )}
                  </span>
                </div>
              </div>
              {blockchainViewing.blockchain.network && (
                <div style={{ display: 'block', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Network</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{blockchainViewing.blockchain.network}</div>
                </div>
              )}
              {blockchainViewing.blockchain.verifiedAt && (
                <div style={{ display: 'block', padding: '8px 0' }}>
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>Verified At</div>
                  <div style={{ fontSize: '14px', fontWeight: '500' }}>{new Date(blockchainViewing.blockchain.verifiedAt).toLocaleString()}</div>
                </div>
              )}
            </div>

            {tamperAlerts.some(alert => alert.appointmentId === blockchainViewing.id && alert.status === 'active') && (
              <div style={{ marginTop: '20px', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '13px', color: '#475569', marginBottom: '12px' }}>
                  If you believe this is an error, please contact our support team for assistance.
                </p>
                <a
                  href={`mailto:auravit2026@gmail.com?subject=Blockchain Tamper Alert - Reference: ${blockchainViewing.reference || 'N/A'}&body=I noticed a blockchain tamper alert for my appointment. Please help me resolve this issue.`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 16px',
                    background: '#3b82f6',
                    color: '#fff',
                    textDecoration: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#2563eb'}
                  onMouseLeave={(e) => e.target.style.background = '#3b82f6'}
                >
                  <FiMail size={16} />
                  Contact Support
                </a>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

    </div>
  );
}